import { useState, type ReactNode } from 'react'
import { NAV_ICON } from './NavIcons'

const RAIL_COLLAPSE_KEY = 'cad-rail-collapsed'

function readRailCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_COLLAPSE_KEY) === 'true'
  } catch {
    return false
  }
}

export interface WorkspaceSwitcherEntry {
  id: string
  name: string
  industryName: string
  role: 'owner' | 'admin' | 'agent' | 'viewer'
}

const COLLAPSE_KEY = 'cad-nav-collapsed'

function readCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? '{}') as Record<string, boolean>
  } catch {
    return {}
  }
}

export interface NavEntry<T extends string> {
  id: T
  label: string
}

/**
 * A titled run of nav items. Nineteen flat entries read as one long list to scan every time;
 * grouped under quiet headings, the eye jumps to the right block first and the item second.
 * A group with no heading (the dashboard) sits above the first divider.
 */
export interface NavGroup<T extends string> {
  heading?: string
  items: NavEntry<T>[]
}

/**
 * The application frame: a dark rail carrying navigation with the workspace pinned at its
 * foot, and a top bar that always states which numbers are live.
 *
 * Phone status sits in the bar rather than on its own screen because a dropped number is
 * silent — replies simply stop arriving — and an agent should not have to go looking to
 * find out.
 */
export function CadenceShell<T extends string>({
  workspaceName,
  workspaceSubtitle,
  userEmail,
  nav,
  current,
  onNavigate,
  phones,
  onPhonesClick,
  tasksDue,
  onTasksClick,
  unreadCount,
  onNotificationsClick,
  onSignOut,
  children,
  fillMain = false,
  workspaceSwitcher,
  demoBanner = false,
}: {
  workspaceName: string
  workspaceSubtitle: string
  userEmail?: string
  nav: NavGroup<T>[]
  current: T
  onNavigate: (id: T) => void
  phones: { connected: number; total: number; degraded: boolean }
  onPhonesClick: () => void
  tasksDue?: { overdue: number; dueSoon: number }
  onTasksClick?: () => void
  unreadCount?: number
  onNotificationsClick?: () => void
  onSignOut: () => void
  children: ReactNode
  /** The active route fills the viewport and manages its own scroll (the inbox), rather than
   *  flowing as a document that scrolls the page. */
  fillMain?: boolean
  /** True only for the shared public preview workspace — a persistent, unmissable reminder that
   *  nothing typed here sends, plus the one link back to actually signing up. */
  demoBanner?: boolean
  /** Present only once there is something to switch between — an account with exactly one
   *  workspace (the common case) gets the plain, non-interactive card it always had, so the
   *  switcher never asks anyone to make a choice with only one real answer. */
  workspaceSwitcher?: {
    workspaces: WorkspaceSwitcherEntry[]
    activeWorkspaceId: string
    onSwitch: (workspaceId: string) => void
    switching?: boolean
  }
}) {
  const initials = workspaceName
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()

  // Which groups the user has collapsed, remembered across navigations and reloads so the rail
  // keeps the shape they left it in rather than springing back open every time a tab changes.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(readCollapsed)
  const [switcherOpen, setSwitcherOpen] = useState(false)
  const canSwitch = Boolean(workspaceSwitcher && workspaceSwitcher.workspaces.length > 1)

  // Defaults open (today's behaviour, unchanged for anyone who never touches the toggle) — the
  // rail only narrows once someone actually asks for the width back, most often to give the
  // inbox's own 3-pane grid more room.
  const [railCollapsed, setRailCollapsed] = useState<boolean>(readRailCollapsed)
  const toggleRail = () =>
    setRailCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(RAIL_COLLAPSE_KEY, String(next))
      } catch {
        // Private-mode: still toggles for the session, just doesn't survive a reload.
      }
      return next
    })
  const toggleGroup = (heading: string) =>
    setCollapsed((prev) => {
      const next = { ...prev, [heading]: !prev[heading] }
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next))
      } catch {
        // A private-mode browser with no storage should still toggle for the session.
      }
      return next
    })

  return (
    <div className="cadence">
      <div className="cad-shell">
        <nav className="cad-rail" data-collapsed={railCollapsed || undefined} aria-label="Workspace">
          <div className="cad-brand">
            <span className="cad-brand-mark" aria-hidden="true">
              W
            </span>
            <span className="cad-brand-text">Wami</span>
            <button
              type="button"
              className="cad-rail-toggle"
              onClick={toggleRail}
              aria-label={railCollapsed ? 'Expand navigation' : 'Collapse navigation'}
              title={railCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path
                  d={railCollapsed ? 'M5 3l4 4-4 4' : 'M9 3L5 7l4 4'}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="cad-nav">
            {nav.map((group, groupIndex) => {
              // The group holding the current screen never hides its items — collapsing away
              // the page you are on would strand you, so it stays open regardless of the toggle.
              const hasActive = group.items.some((entry) => entry.id === current)
              const isCollapsed = !railCollapsed && Boolean(group.heading) && collapsed[group.heading!] && !hasActive
              return (
                <div
                  className="cad-nav-group"
                  data-collapsed={isCollapsed || undefined}
                  key={group.heading ?? `group-${groupIndex}`}
                >
                  {/* A collapsed rail is a flat icon stack — Periskope's own shape for this —
                      so the group headings that make sense with text labels are dropped rather
                      than rendered as a bare, unreadable divider between icons. */}
                  {group.heading && !railCollapsed && (
                    <button
                      type="button"
                      className="cad-nav-heading"
                      aria-expanded={!isCollapsed}
                      onClick={() => toggleGroup(group.heading!)}
                    >
                      <span>{group.heading}</span>
                      <svg
                        className="cad-nav-chevron"
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        aria-hidden="true"
                      >
                        <path
                          d="M2 3.5 L5 6.5 L8 3.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                  {!isCollapsed &&
                    group.items.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        className="cad-nav-item"
                        aria-current={current === entry.id ? 'page' : undefined}
                        onClick={() => onNavigate(entry.id)}
                        title={railCollapsed ? entry.label : undefined}
                      >
                        <span className="cad-nav-icon" aria-hidden="true">
                          {NAV_ICON[entry.id] ?? NAV_ICON.settings}
                        </span>
                        <span className="cad-nav-label">{entry.label}</span>
                      </button>
                    ))}
                </div>
              )
            })}
          </div>

          <div
            className="cad-org"
            style={canSwitch ? { position: 'relative' } : undefined}
            title={railCollapsed && !canSwitch ? workspaceName : undefined}
          >
            {canSwitch ? (
              <button
                type="button"
                className="cad-org-switch"
                onClick={() => setSwitcherOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={switcherOpen}
                title={railCollapsed ? `${workspaceName} · switch workspace` : undefined}
              >
                <span className="cad-org-mark" aria-hidden="true">
                  {initials}
                </span>
                <span className="stack" style={{ minWidth: 0, gap: '1px' }}>
                  <span className="cad-org-name">{workspaceName}</span>
                  <span className="cad-org-sub">{workspaceSubtitle}</span>
                </span>
                <svg className="cad-nav-chevron" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                  <path d="M2 6.5 L5 3.5 L8 6.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : (
              <>
                <span className="cad-org-mark" aria-hidden="true">
                  {initials}
                </span>
                <span className="stack" style={{ minWidth: 0, gap: '1px' }}>
                  <span className="cad-org-name">{workspaceName}</span>
                  <span className="cad-org-sub">{workspaceSubtitle}</span>
                </span>
              </>
            )}

            {canSwitch && switcherOpen && (
              <div className="cad-card cad-popover" style={{ bottom: 'calc(100% + 6px)', top: 'auto', left: 0, right: 'auto', width: '240px' }}>
                <div className="stack gap-1">
                  {workspaceSwitcher!.workspaces.map((ws) => (
                    <button
                      key={ws.id}
                      type="button"
                      className="cad-nav-item"
                      data-selected={ws.id === workspaceSwitcher!.activeWorkspaceId || undefined}
                      disabled={workspaceSwitcher!.switching}
                      onClick={() => {
                        setSwitcherOpen(false)
                        if (ws.id !== workspaceSwitcher!.activeWorkspaceId) workspaceSwitcher!.onSwitch(ws.id)
                      }}
                    >
                      <span className="stack" style={{ gap: '1px', alignItems: 'flex-start' }}>
                        <span>{ws.name}</span>
                        <span className="cad-org-sub">{ws.industryName} · {ws.role}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="cad-content">
          {demoBanner && (
            <div className="cad-demo-banner">
              <span>You're viewing a live demo — nothing you send here actually goes out.</span>
              <a href="/signup" className="cad-btn" data-variant="text" data-tone="accent">
                Sign up free
              </a>
            </div>
          )}
          <header className="cad-topbar">
            {/*
              Stated as a fraction rather than a tick: "1 / 4" makes a number that dropped
              overnight visible at a glance, where a green dot would not.
            */}
            <button
              type="button"
              className="cad-pill"
              data-tone={phones.degraded ? 'bad' : phones.connected > 0 ? 'good' : 'warn'}
              onClick={onPhonesClick}
              title="WhatsApp numbers"
            >
              <span className="cad-dot" aria-hidden="true" />
              {phones.connected} / {phones.total} numbers
            </button>

            {/* Same reasoning as the phone pill: an overdue task is silent until someone goes
                looking for it, so its count sits where it cannot be missed. */}
            {tasksDue && (tasksDue.overdue > 0 || tasksDue.dueSoon > 0) && (
              <button
                type="button"
                className="cad-pill"
                data-tone={tasksDue.overdue > 0 ? 'bad' : 'warn'}
                onClick={onTasksClick}
                title="Tasks"
              >
                {tasksDue.overdue > 0 ? `${tasksDue.overdue} task${tasksDue.overdue === 1 ? '' : 's'} overdue` : `${tasksDue.dueSoon} due soon`}
              </button>
            )}

            {Boolean(unreadCount) && (
              <button
                type="button"
                className="cad-pill"
                data-tone="accent"
                onClick={onNotificationsClick}
                title="Notifications"
              >
                {unreadCount} notification{unreadCount === 1 ? '' : 's'}
              </button>
            )}

            <span className="grow" />

            <div className="cad-account">
              {userEmail && (
                <>
                  <span className="cad-avatar" aria-hidden="true">
                    {userEmail[0]?.toUpperCase()}
                  </span>
                  <span className="cad-muted user-email" title={userEmail}>
                    {userEmail}
                  </span>
                  <span className="cad-account-divider" aria-hidden="true" />
                </>
              )}
              <button type="button" className="cad-btn" data-variant="text" onClick={onSignOut}>
                Sign out
              </button>
            </div>
          </header>

          <main className={fillMain ? 'cad-main cad-main-fill' : 'cad-main'}>{children}</main>
        </div>
      </div>
    </div>
  )
}
